import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { Box } from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";

import { currentProfileAtom } from "../../../../features/Club/atoms";
import { useFlags } from "../../../../utils/launchDarkly";
import { EventDetailActions } from "../EventDetailActions";
import { EventDetailAttendeesList } from "../EventDetailAttendeesList";
import { EventDetailGroupDisplay } from "../EventDetailGroupDisplay";
import { EventDetailJoinEvent } from "../EventDetailJoinEvent";
import { EventDetailPricingSection } from "../EventDetailPricingSection";
import { EventDetailSeriesInfoBanner } from "../EventDetailSeriesInfoBanner/EventDetailSeriesInfoBanner";
import { EventDetailSignUpCard } from "../EventDetailSignUpCard";
import { NewEventDetailAttendeesList } from "../NewEventDetailAttendeesList";
import { EventDetailYourSignup } from "./EventDetailYourSignup";
import { EventDetailSignUpSection_event$key } from "./__generated__/EventDetailSignUpSection_event.graphql";

type EventDetailSignUpSectionProps = {
  fragmentRef: EventDetailSignUpSection_event$key;
};

export const EventDetailSignUpSection = ({
  fragmentRef,
}: EventDetailSignUpSectionProps) => {
  const data = useFragment(EventDetailSignUpSectionFragmentString, fragmentRef);

  const { newPatronEventBookingFlow } = useFlags();
  const currentProfile = useAtomValue(currentProfileAtom);

  const hasSignup = !currentProfile?.memberId
    ? null
    : ["CHECKED_IN", "CONFIRMED", "WAITLIST"].includes(
        data?.signUpStatusForClient ?? ""
      ) ||
      (data.memberSignUpProxyAllowed &&
        data.signUps?.edges?.find(
          (e) => e?.node?.ownerClient?.id === currentProfile?.memberId
        ));

  const isLargeScreen = useBreakpointValue({
    base: false,
    xs: false,
    sm: false,
    md: false,
    lg: true,
  });

  const supportsPublic = data.eventSeries?.facility?.club?.supportsPublic;

  return (
    <Box px={{ base: 6, md: 0 }} flex={1}>
      {hasSignup ? (
        newPatronEventBookingFlow ? (
          <EventDetailYourSignup fragmentRef={data} />
        ) : (
          <>
            <EventDetailGroupDisplay fragmentRef={data} />
            <EventDetailActions fragmentRef={data} />
          </>
        )
      ) : newPatronEventBookingFlow ? (
        <Box pt={4}>
          <EventDetailSignUpCard eventRef={data} />
          <EventDetailSeriesInfoBanner fragmentRef={data} />
        </Box>
      ) : (
        <Box pt={4}>
          <EventDetailPricingSection eventRef={data} />
          {isLargeScreen ? (
            <Box pt={2} pb={6}>
              <EventDetailJoinEvent eventRef={data} />
            </Box>
          ) : null}

          <EventDetailSeriesInfoBanner fragmentRef={data} />
        </Box>
      )}
      {supportsPublic ||
      data.attendeeListVisible === false ? null : newPatronEventBookingFlow ? (
        <NewEventDetailAttendeesList fragmentRef={data} />
      ) : (
        <EventDetailAttendeesList fragmentRef={data} />
      )}
    </Box>
  );
};

const EventDetailSignUpSectionFragmentString = graphql`
  fragment EventDetailSignUpSection_event on Event {
    signUpStatusForClient(clientId: $memberId)
    eventSeries {
      facility {
        club {
          supportsPublic
        }
      }
    }
    signUps {
      edges {
        node {
          id
          ownerClient {
            ... on Member {
              id
            }
            ... on Customer {
              id
            }
          }
        }
      }
    }
    memberSignUpProxyAllowed
    attendeeListVisible
    ...EventDetailPricingSection_event
    ...EventDetailJoinEvent_event
    ...EventDetailSignUpCard_event
    ...EventDetailGroupDisplay_event
    ...EventDetailSeriesInfoBanner
    ...EventDetailActions_event
    ...EventDetailYourSignup_event
    ...EventDetailAttendeesList
    ...NewEventDetailAttendeesList
  }
`;
